import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  FolderKanban,
  Plus,
  ReceiptText,
  Wallet,
  Zap,
} from 'lucide-react';
import { match, type MatchAccount, type MatchEntry } from '../api';
import { useReadOnly } from '../ui/AppShell';
import { MatchShell } from './MatchShell';
import { ActionQueueCard, MetricStrip, TimelineRow } from '../ui/mobileOps';
import { Button, EmptyState, Pill, Spinner } from '../ui/primitives';
import { SectionCard } from '../ui/components';

function formatInr(raw: string): string {
  if (!raw) return '0';
  const neg = raw.startsWith('-');
  const abs = neg ? raw.slice(1) : raw;
  const [intPart, fracPart] = abs.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = fracPart ? `${withCommas}.${fracPart}` : withCommas;
  return `${neg ? '-' : ''}₹${formatted}`;
}

function absDecimal(raw: string): number {
  if (!raw) return 0;
  const n = Number(raw.startsWith('-') ? raw.slice(1) : raw);
  return Number.isFinite(n) ? n : 0;
}

export function MatchHomePage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts-active'],
    queryFn: () => match.listAccounts({ status: 'active' }),
  });
  const balancesQ = useQuery<Array<{ matchAccountId: number; balance: string }>>({
    queryKey: ['match-balances'],
    queryFn: () => match.listBalances(),
  });
  const recentEntriesQ = useQuery<MatchEntry[]>({
    queryKey: ['match-entries-recent'],
    queryFn: () => match.listEntries({ limit: 10 }),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((account) => [account.id, account])),
    [accountsQ.data],
  );

  const topAccounts = useMemo(() => {
    const accounts = accountsQ.data ?? [];
    const balanceById = new Map((balancesQ.data ?? []).map((item) => [item.matchAccountId, item.balance]));
    return accounts
      .filter((account) => account.status === 'active')
      .map((account) => ({ account, balance: balanceById.get(account.id) ?? '0' }))
      .sort((left, right) => absDecimal(right.balance) - absDecimal(left.balance))
      .slice(0, 5);
  }, [accountsQ.data, balancesQ.data]);

  const activeAccountCount = useMemo(
    () => (accountsQ.data ?? []).filter((account) => account.status === 'active').length,
    [accountsQ.data],
  );
  const netBalance = useMemo(
    () => (balancesQ.data ?? []).reduce((sum, item) => sum + Number(item.balance || '0'), 0),
    [balancesQ.data],
  );
  const recentCount = (recentEntriesQ.data ?? []).length;
  const hasAccounts = (accountsQ.data ?? []).length > 0;
  const primaryAccount = topAccounts[0]?.account ?? null;

  return (
    <MatchShell
      subtitle="Quick entry, recent movement, and top accounts"
      actions={
        !readOnly ? (
          <Button size="sm" onClick={() => nav('/match/entry')}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>Quick entry</span>
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="order-2 md:order-1">
          {!accountsQ.isLoading && !balancesQ.isLoading && (
            <MetricStrip
            section="match"
            items={[
              { label: 'Accounts', value: String(activeAccountCount), note: 'active in use', to: '/match/accounts' },
              {
                label: 'Net balance',
                value: netBalance.toFixed(2),
                note: 'across accounts',
                tone: netBalance < 0 ? 'neg' : netBalance > 0 ? 'pos' : 'default',
              },
              { label: 'Recent', value: String(recentCount), note: 'latest entries' },
            ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2 grid gap-3 lg:grid-cols-[1.15fr_0.85fr]">
          <ActionQueueCard
            section="match"
            eyebrow="Daily flow"
            title={primaryAccount ? `Entry flow starts with ${primaryAccount.name}` : 'Create a match account first'}
            description={
              primaryAccount
                ? 'Quick Entry remains the fastest way to record signed movement on phone. Top accounts stay right below it.'
                : 'The match console becomes active once you create the first account.'
            }
            actions={
              <>
                {!readOnly && (
                  <Button size="sm" onClick={() => nav('/match/entry')} disabled={!hasAccounts && accountsQ.isFetched}>
                    Quick Entry
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => nav('/match/accounts')}>
                  Accounts
                </Button>
              </>
            }
          />

          <ActionQueueCard
            section="match"
            eyebrow="Watchlist"
            title={
              netBalance === 0
                ? 'Accounts are flat overall'
                : netBalance > 0
                ? 'Positive movement dominates'
                : 'Negative movement dominates'
            }
            description="Recent activity and top balances use the same stacked mobile rhythm as Cash and Ledgers."
            tone="neutral"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard
            section="match"
            title="Recent activity"
            action={
              <Link to="/match/accounts" className="text-caption font-medium text-sec-match hover:underline">
                All accounts
              </Link>
            }
          >
            {recentEntriesQ.isLoading ? (
              <div className="flex items-center gap-2 text-small text-ink-muted">
                <Spinner /> Loading recent entries…
              </div>
            ) : (recentEntriesQ.data ?? []).length === 0 ? (
              <EmptyState
                section="match"
                icon={ReceiptText}
                title="No entries yet"
                description="Submit the first quick entry to see activity here."
              />
            ) : (
              <div className="space-y-3">
                {(recentEntriesQ.data ?? []).map((entry) => {
                  const account = accountsById.get(entry.sourceMatchAccountId);
                  return (
                    <Link key={entry.id} to={`/match/entries/${entry.id}`} className="block">
                      <TimelineRow
                        section="match"
                        title={account?.name ?? `#${entry.sourceMatchAccountId}`}
                        subtitle={entry.note?.trim() || 'Signed amount posted'}
                        date={entry.effectiveDate}
                        amount={formatInr(entry.amount)}
                        amountTone={entry.amount.startsWith('-') ? 'neg' : 'pos'}
                        badges={
                          <>
                            {entry.voidedAt && <Pill tone="red">Voided</Pill>}
                            {entry.hadOverride && !entry.voidedAt && <Pill section="match">Override</Pill>}
                          </>
                        }
                        action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                      />
                    </Link>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard
            section="match"
            title="Top accounts"
            action={
              <Link to="/match/accounts" className="text-caption font-medium text-sec-match hover:underline">
                Manage
              </Link>
            }
          >
            {accountsQ.isLoading || balancesQ.isLoading ? (
              <div className="flex items-center gap-2 text-small text-ink-muted">
                <Spinner /> Loading accounts…
              </div>
            ) : topAccounts.length === 0 ? (
              <EmptyState
                section="match"
                icon={FolderKanban}
                title="No accounts yet"
                description="Create a match account to start using quick entry and split distribution."
              />
            ) : (
              <div className="space-y-3">
                {topAccounts.map(({ account, balance }) => (
                  <Link key={account.id} to={`/match/accounts/${account.id}/ledger`} className="block">
                    <TimelineRow
                      section="match"
                      title={account.name}
                      subtitle={account.status}
                      date="Current balance"
                      amount={formatInr(balance)}
                      amountTone={balance.startsWith('-') ? 'neg' : balance === '0' ? 'default' : 'pos'}
                      action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                    />
                  </Link>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </MatchShell>
  );
}
